import {
  AcAlert,
  AcAppHeader,
  AcButton,
  AcFormProvider,
  AcIcon,
  AcTab,
  AcTabs,
} from '@components'
import {
  GLOBAL_ERRORS,
  ICONS,
  KEYS,
  LABELS,
  ROUTE_PATHS,
  SUPPLIER_DETAIL_TABS,
} from '@constants'
import { useStore } from '@hooks/use-store'
import { ISupplierInformationTab } from '@partials/tabs/supplier-detail/supplier-information'
import { ISupplier } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { FieldValues, useForm } from 'react-hook-form'

import { generatePath, useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
export const AcSupplierCreate = observer((): JSX.Element => {
  const { suppliers } = useStore()
  const locationParams = useParams()
  const navigate = useNavigate()

  const [tabErrors, setTabErrors] = useState(new Set<string>())
  const [editMode, setEditMode] = useState(true)

  const handleSuccess = (newSupplier: ISupplier) => {
    return new Promise(resolve => {
      setTabErrors(new Set())
      setEditMode(false)
      form.reset(newSupplier)
      toast(LABELS.SAVED_SUPPLIER, {
        toastId: KEYS.SAVED_SUPPLIER,
        icon: <AcIcon icon={ICONS.CHECK} />,
      })
      resolve(null)
    })
  }

  const handleErrors = (errors: Record<string, string[]>) => {
    const errorKeys = Object.keys(errors)

    errorKeys.map(singleKey =>
      form.setError(singleKey as string, {
        type: 'custom',
        message: errors[singleKey][0],
      })
    )
  }

  const onSubmit = async (data: FieldValues) => {
    setTabErrors(new Set())
    await suppliers
      .createSupplier(data as ISupplier)
      .then(async (newSupplier: ISupplier) => {
        await handleSuccess(newSupplier)
        const path = generatePath(ROUTE_PATHS.SUPPLIER_DETAIL, {
          id: newSupplier.id.toString(),
        })
        navigate(path, { replace: true })
      })
      .catch(e => {
        handleErrors(e)
      })
  }

  const hasErrors = useMemo(() => !!tabErrors.size, [tabErrors])

  const setTabError = (name: string) => {
    setTabErrors(value => new Set(...value).add(name))
  }

  const form = useForm()

  const onFormSubmit = () => {
    form.handleSubmit(onSubmit)()
  }

  useEffect(() => {
    suppliers.setForm(form)
    return () => suppliers.clearSupplier()
  }, [locationParams?.id])

  return (
    <>
      <AcAppHeader
        showBackButton
        navigateBackTo={ROUTE_PATHS.SUPPLIER_LIST}
        active={suppliers.supplier.active ?? false}
        title={suppliers.supplier.name || ''}
        action={
          <AcButton
            onClick={editMode ? onFormSubmit : () => setEditMode(true)}
            label={editMode ? LABELS.SAVE : LABELS.MODIFY}
          />
        }
      />
      <AcAlert
        title={GLOBAL_ERRORS.FORM_STATE_INVALID}
        visible={hasErrors}
      />
      <AcFormProvider
        form={form}
        key={locationParams.id}
        onSubmit={onFormSubmit}
        readOnly={!editMode}
        loading={suppliers.loading}>
        <AcTabs
          tabs={SUPPLIER_DETAIL_TABS}
          errors={tabErrors}>
          <AcTab<ISupplierInformationTab>
            name={KEYS.SUPPLIER_INFORMATION}
            onError={() => setTabError(KEYS.SUPPLIER_INFORMATION)}
          />
          <AcTab
            name={KEYS.SUPPLIER_CONTACTS}
            onError={() => setTabError(KEYS.SUPPLIER_CONTACTS)}
          />
        </AcTabs>
      </AcFormProvider>
    </>
  )
})
